<template>
  <ContentWrap>
    <el-form :inline="true" :model="query" class="-mb-15px">
      <el-form-item label="类型">
        <el-select v-model="query.kind" clearable class="!w-120px" placeholder="全部">
          <el-option label="企业" value="company" />
          <el-option label="商会" value="assoc" />
        </el-select>
      </el-form-item>
      <el-form-item label="关键字">
        <el-input
          v-model="query.keyword"
          clearable
          class="!w-220px"
          placeholder="名称/行业"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="城市">
        <el-input v-model="query.city" clearable class="!w-140px" placeholder="城市" @keyup.enter="search" />
      </el-form-item>
      <el-form-item label="归属状态">
        <el-select v-model="query.claimStatus" clearable class="!w-140px" placeholder="全部">
          <el-option label="已认领" value="claimed" />
          <el-option label="待认领" value="claimable" />
        </el-select>
      </el-form-item>
      <el-form-item label="状态">
        <el-select v-model="query.status" clearable class="!w-120px" placeholder="全部">
          <el-option label="正常" :value="0" />
          <el-option label="已下架" :value="1" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <div class="mb-12px flex items-center justify-between">
      <el-button
        v-hasPermi="['bosscircle:resource:create']"
        type="primary"
        @click="openForm()"
      >
        <Icon icon="ep:plus" class="mr-5px" />新增资源
      </el-button>
      <div class="flex items-center gap-10px">
        <el-button @click="goClaim">认领审核</el-button>
        <el-button @click="goApply">入驻审核</el-button>
      </div>
    </div>

    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column label="资源" min-width="240">
        <template #default="{ row }">
          <div class="flex items-center gap-10px">
            <el-avatar :size="40" :src="row.logo || undefined" shape="square">
              <Icon icon="ep:office-building" />
            </el-avatar>
            <div class="min-w-0">
              <div class="font-500 truncate">
                {{ row.name }}
                <el-tag v-if="row.verified" class="ml-5px" size="small" type="success">已认证</el-tag>
              </div>
              <div class="text-12px text-#909399 truncate">
                {{ row.shortName || '-' }} · {{ row.industry || '未填行业' }}
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="类型" width="80">
        <template #default="{ row }">
          <el-tag :type="row.kind === 'assoc' ? 'warning' : 'primary'" size="small">
            {{ row.kind === 'assoc' ? '商会' : '企业' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="城市" width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.city || '-' }}{{ row.district ? ' · ' + row.district : '' }}</template>
      </el-table-column>
      <el-table-column label="归属" width="90">
        <template #default="{ row }">
          <el-tag :type="row.claimStatus === 'claimed' ? 'success' : 'info'" size="small">
            {{ row.claimStatus === 'claimed' ? '已认领' : '待认领' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="score" label="评分" width="80" />
      <el-table-column label="数据" width="150">
        <template #default="{ row }">
          <span class="text-12px text-#909399">
            浏览 {{ row.viewCount ?? 0 }} · 关注 {{ row.followCount ?? 0 }} · 收藏
            {{ row.favoriteCount ?? 0 }}
          </span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="row.status === 1 ? 'info' : 'success'" size="small">
            {{ row.status === 1 ? '已下架' : '正常' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="createTime" label="创建时间" width="170" :formatter="dateFormatter" />
      <el-table-column label="操作" width="200" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <el-button
            v-hasPermi="['bosscircle:resource:update']"
            link
            type="primary"
            @click="openForm(row)"
          >
            编辑
          </el-button>
          <el-button
            v-hasPermi="['bosscircle:resource:update']"
            link
            :type="row.status === 1 ? 'success' : 'warning'"
            @click="toggleStatus(row)"
          >
            {{ row.status === 1 ? '上架' : '下架' }}
          </el-button>
          <el-button
            v-hasPermi="['bosscircle:resource:delete']"
            link
            type="danger"
            @click="handleDelete(row)"
          >
            删除
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <!-- 详情抽屉 -->
  <el-drawer v-model="detailVisible" title="资源详情" size="720px">
    <el-descriptions v-if="detail.id" :column="2" border>
      <el-descriptions-item label="名称">{{ detail.name }}</el-descriptions-item>
      <el-descriptions-item label="简称">{{ detail.shortName || '-' }}</el-descriptions-item>
      <el-descriptions-item label="类型">
        {{ detail.kind === 'assoc' ? '商会' : '企业' }}
      </el-descriptions-item>
      <el-descriptions-item label="行业">{{ detail.industry || '-' }}</el-descriptions-item>
      <el-descriptions-item label="统一信用代码">{{ detail.creditCode || '-' }}</el-descriptions-item>
      <el-descriptions-item label="法定代表人">{{ detail.legalPerson || '-' }}</el-descriptions-item>
      <el-descriptions-item label="注册资本">{{ detail.capital || '-' }}</el-descriptions-item>
      <el-descriptions-item label="成立时间">{{ detail.founded || '-' }}</el-descriptions-item>
      <el-descriptions-item label="城市">{{ detail.city || '-' }}</el-descriptions-item>
      <el-descriptions-item label="区县">{{ detail.district || '-' }}</el-descriptions-item>
      <el-descriptions-item label="规模">{{ detail.scale || '-' }}</el-descriptions-item>
      <el-descriptions-item label="经营范围" :span="2">{{ detail.scope || '-' }}</el-descriptions-item>
      <el-descriptions-item label="地址" :span="2">{{ detail.address || '-' }}</el-descriptions-item>
      <el-descriptions-item label="官网">{{ detail.website || '-' }}</el-descriptions-item>
      <el-descriptions-item label="联系电话">{{ detail.phoneMasked || detail.phone || '-' }}</el-descriptions-item>
      <el-descriptions-item label="归属用户ID">{{ detail.ownerUserId || '-' }}</el-descriptions-item>
      <el-descriptions-item label="来源">{{ detail.source || '-' }}</el-descriptions-item>
      <el-descriptions-item label="简介" :span="2">{{ detail.intro || '-' }}</el-descriptions-item>
    </el-descriptions>
    <div v-if="detail.logo" class="mt-16px">
      <div class="mb-8px text-14px text-#909399">Logo</div>
      <el-image :src="detail.logo" style="width: 120px" :preview-src-list="[detail.logo]" />
    </div>
  </el-drawer>

  <!-- 新增/编辑 -->
  <Dialog :title="form.id ? '编辑资源' : '新增资源'" v-model="formVisible" width="820px">
    <el-form ref="formRef" :model="form" :rules="rules" label-width="90px" class="pr-20px">
      <el-row :gutter="16">
        <el-col :span="12">
          <el-form-item label="类型" prop="kind">
            <el-radio-group v-model="form.kind">
              <el-radio value="company">企业</el-radio>
              <el-radio value="assoc">商会</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="名称" prop="name">
            <el-input v-model="form.name" placeholder="资源全称" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="简称">
            <el-input v-model="form.shortName" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="行业">
            <el-select
              v-model="form.industry"
              class="w-100%"
              clearable
              filterable
              default-first-option
              placeholder="下拉选择，可直接输入行业名实时匹配"
            >
              <el-option v-for="name in industryOptions" :key="name" :label="name" :value="name" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="城市">
            <el-input v-model="form.city" placeholder="地图选点自动带出，可手工修正" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="区县">
            <el-input v-model="form.district" placeholder="地图选点自动带出，可手工修正" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="信用代码">
            <el-input v-model="form.creditCode" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="法定代表人">
            <el-autocomplete
              v-model="form.legalPerson"
              class="w-100%"
              :fetch-suggestions="queryLegalPerson"
              :trigger-on-focus="false"
              :debounce="300"
              placeholder="输入姓名实时匹配系统会员，选不中可直接手输"
              @select="handleLegalPersonSelect"
            >
              <template #default="{ item }">
                <div class="flex items-center justify-between gap-24px">
                  <span>{{ item.value }}</span>
                  <span class="text-12px text-gray-400">{{ item.mobile || '未填手机号' }}</span>
                </div>
              </template>
            </el-autocomplete>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="Logo">
            <UploadImg
              v-model="form.logo"
              directory="bosscircle/resource"
              height="80px"
              width="80px"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="联系电话" prop="phone">
            <el-input v-model="form.phone" placeholder="选中会员后自动带入，可手工修正" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="归属状态">
            <el-select v-model="form.claimStatus" class="w-100%">
              <el-option label="已认领" value="claimed" />
              <el-option label="待认领" value="claimable" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="状态">
            <el-select v-model="form.status" class="w-100%">
              <el-option label="正常" :value="0" />
              <el-option label="已下架" :value="1" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item label="简介">
            <el-input v-model="form.intro" type="textarea" :rows="3" />
          </el-form-item>
        </el-col>
        <el-col :span="24">
          <el-form-item label="地址">
            <div class="flex w-full items-center gap-12px">
              <el-input
                v-model="form.address"
                class="flex-1"
                placeholder="点右侧「地图选点」搜企业名或地址，自动回填城市 / 区县 / 详细地址"
              />
              <el-button type="primary" @click="openMap">地图选点</el-button>
            </div>
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>
    <template #footer>
      <el-button @click="formVisible = false">取 消</el-button>
      <el-button :loading="saving" type="primary" @click="submitForm">确 定</el-button>
    </template>
  </Dialog>

  <AmapDialog ref="amapRef" @confirm="handleMapConfirm" />
</template>

<script setup lang="ts">
import { dateFormatter } from '@/utils/formatTime'
import * as Api from '@/api/business/resource'
import { getDictOptions } from '@/utils/dict'
import { AmapDialog } from '@/components/Amap'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'BossCircleResource' })

const message = useMessage()
const router = useRouter()

const loading = ref(false)
const saving = ref(false)
const list = ref<Api.ResourceVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  kind: undefined as Api.ResourceKind | undefined,
  keyword: '',
  city: '',
  claimStatus: undefined as Api.ResourceClaimStatus | undefined,
  status: undefined as number | undefined
})

const load = async () => {
  loading.value = true
  try {
    const params: any = {
      pageNo: query.pageNo,
      pageSize: query.pageSize,
      kind: query.kind,
      status: query.status
    }
    if (query.keyword?.trim()) params.keyword = query.keyword.trim()
    if (query.city?.trim()) params.city = query.city.trim()
    if (query.claimStatus) params.claimStatus = query.claimStatus
    const data = await Api.getResourcePage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  query.kind = undefined
  query.keyword = ''
  query.city = ''
  query.claimStatus = undefined
  query.status = undefined
  search()
}

// ============ 详情 ============
const detailVisible = ref(false)
const detail = ref<Api.ResourceVO>({})

const openDetail = (row: Api.ResourceVO) => {
  detail.value = row
  detailVisible.value = true
}

// ============ 新增/编辑 ============
/** 行业唯一来源是字典 bosscircle_industry（后台「行业配置」维护）；资源表存的是行业名，C 端也按名称匹配，所以取 label 当值 */
const INDUSTRY_DICT_TYPE = 'bosscircle_industry'
const industryOptions = computed(() =>
  getDictOptions(INDUSTRY_DICT_TYPE)
    .map((d: any) => String(d.label ?? '').trim())
    .filter(Boolean)
)

const formVisible = ref(false)
const formRef = ref<FormInstance>()
const form = reactive<any>({
  id: undefined,
  kind: 'company',
  name: '',
  shortName: '',
  industry: '',
  city: '',
  district: '',
  creditCode: '',
  legalPerson: '',
  logo: '',
  phone: '',
  claimStatus: 'claimable',
  status: 0,
  intro: '',
  address: ''
})

const PHONE_PATTERN = /^1[3-9]\d{9}$/
/** 带区号的座机，如 0755-12345678 / 075512345678，可带分机 */
const TEL_PATTERN = /^(?:0\d{2,3}-?)?[2-9]\d{6,7}(?:-\d{1,6})?$/

const rules = {
  name: [{ required: true, message: '资源名称不能为空', trigger: 'blur' }],
  kind: [{ required: true, message: '资源类型不能为空', trigger: 'change' }],
  phone: [
    {
      validator: (_rule: any, value: any, callback: (error?: Error) => void) => {
        const phone = String(value ?? '').trim()
        // 电话非必填，但填了就必须是手机号或座机：手输的法定代表人没有会员号码可带入，只能靠校验兜住
        if (!phone || PHONE_PATTERN.test(phone) || TEL_PATTERN.test(phone)) {
          callback()
          return
        }
        callback(new Error('请填写正确的手机号或座机号'))
      },
      trigger: 'blur'
    }
  ]
} as FormRules

// ============ 法定代表人：实时匹配系统会员，选中后自动带入联系电话 ============
const queryLegalPerson = async (
  queryString: string,
  cb: (candidates: Array<{ value: string; mobile?: string }>) => void
) => {
  const keyword = queryString?.trim()
  if (!keyword) {
    cb([])
    return
  }
  try {
    const candidates: Api.ResourceUserCandidateVO[] = await Api.getResourceUserCandidates(keyword)
    cb(
      (candidates || [])
        .map((item) => ({ value: item.name || item.nickname || '', mobile: item.mobile }))
        .filter((item) => item.value)
    )
  } catch {
    // 匹配失败绝不能挡住手输，接口异常时退化成普通文本框
    cb([])
  }
}

const handleLegalPersonSelect = (item: { value: string; mobile?: string }) => {
  if (item.mobile) {
    form.phone = item.mobile
    message.success('已带入该会员的联系电话')
  }
}

// ============ 地图选点：城市 / 区县 / 详细地址由高德选点回填，仍可手工修正 ============
const amapRef = ref<InstanceType<typeof AmapDialog>>()
const mapCenter = ref<{ lng: number; lat: number } | undefined>()

const openMap = () => {
  amapRef.value?.open(mapCenter.value?.lng, mapCenter.value?.lat)
}

const handleMapConfirm = (data: {
  longitude: number
  latitude: number
  address: string
  province: string
  city: string
  district: string
}) => {
  mapCenter.value = { lng: data.longitude, lat: data.latitude }
  if (data.city) form.city = data.city
  if (data.district) form.district = data.district
  if (data.address) form.address = data.address
  message.success('已按地图选点回填城市、区县与详细地址')
}

const openForm = (row?: Api.ResourceVO) => {
  Object.assign(form, {
    id: undefined,
    kind: 'company',
    name: '',
    shortName: '',
    industry: '',
    city: '',
    district: '',
    creditCode: '',
    legalPerson: '',
    logo: '',
    phone: '',
    claimStatus: 'claimable',
    status: 0,
    intro: '',
    address: ''
  })
  if (row?.id) Object.assign(form, row)
  // 资源表不存经纬度，重新打开时回到默认中心，由地图里的地址搜索定位
  mapCenter.value = undefined
  formVisible.value = true
  nextTick(() => formRef.value?.clearValidate())
}

const submitForm = async () => {
  await formRef.value?.validate()
  saving.value = true
  try {
    if (form.id) {
      await Api.updateResource(form)
      message.success('更新成功')
    } else {
      await Api.createResource(form)
      message.success('创建成功')
    }
    formVisible.value = false
    load()
  } finally {
    saving.value = false
  }
}

// ============ 上下架 / 删除 ============
const toggleStatus = async (row: Api.ResourceVO) => {
  const next = row.status === 1 ? 0 : 1
  await Api.updateResource({ id: row.id, status: next } as any)
  message.success(next === 1 ? '已下架' : '已上架')
  load()
}

const handleDelete = async (row: Api.ResourceVO) => {
  await message.delConfirm(`确认删除资源「${row.name}」吗？`)
  await Api.deleteResource(row.id!)
  message.success('删除成功')
  load()
}

// ============ 跳转审核页 ============
const goClaim = () =>
  router.push({ name: 'BossCircleResourceClaim', query: { status: 'pending' } })
const goApply = () =>
  router.push({ name: 'BossCircleResourceApply', query: { status: 'pending' } })

onMounted(load)
</script>